import { format, addDays, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DeliveryCard from './DeliveryCard';
import DayHeader from './DayHeader';

// Remplacer preferences par livreurColors dans les props
export default function DayView({ day, onDayChange, deliveries, users, dayInfos, teamLocations,
                                    livreurColors, onCardClick, onPrepChange, onReload, filtreLibreur, onDragEnd,
                                    masquerPauseHospitalise }) {
    const dateStr = format(day, 'yyyy-MM-dd');

    const filterDeliveries = (periode) => deliveries
        .filter(d => {
            const dateMatch = d.date?.startsWith(dateStr);
            const periodeMatch = d.periode === periode;
            const livreurMatch = !filtreLibreur || d.livreur?.['@id'] === filtreLibreur;
            const fttMatch = d.patient?.statutPatient !== 'ftt';
            const pauseHospitaliseMatch = !masquerPauseHospitalise || (
                d.patient?.statutPatient !== 'en_pause' && d.patient?.statutPatient !== 'hospitalise'
            );
            return dateMatch && periodeMatch && livreurMatch && fttMatch && pauseHospitaliseMatch;
        })
        .sort((a, b) => {
            const livreurA = a.livreur?.nom || '';
            const livreurB = b.livreur?.nom || '';
            if (livreurA !== livreurB) return livreurA.localeCompare(livreurB);
            return (a.heureDebut || '').localeCompare(b.heureDebut || '');
        });

    const matin = filterDeliveries('matin');
    const aprem = filterDeliveries('aprem');

    const dayInfo = dayInfos.find(d => d.date?.startsWith(dateStr)) || null;
    const dayTeamLocations = teamLocations.filter(t => t.date?.startsWith(dateStr));

    const PeriodeColumn = ({ titre, droppableId, items }) => {
        // Regroupe les indices par groupe de livreur consécutif
        const groups = [];
        items.forEach((delivery, index) => {
            const livreurId = delivery.livreur?.['@id'] || (delivery.livreur?.id ? `/api/users/${delivery.livreur.id}` : null);
            const lastGroup = groups[groups.length - 1];
            if (lastGroup && lastGroup.livreurId === livreurId) {
                lastGroup.items.push({ delivery, index });
            } else {
                groups.push({ livreurId, items: [{ delivery, index }] });
            }
        });

        return (
            <div style={{
                flex: 1,
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: isToday(day) ? '0 0 0 2px var(--accent-color)' : '0 1px 6px rgba(0,0,0,0.07)',
                border: isToday(day) ? '2px solid var(--accent-color)' : '2px solid transparent',
            }}>
                <div style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    color: '#888',
                    marginBottom: '12px',
                    letterSpacing: '0.05em',
                }}>
                    {titre} ({items.length})
                </div>
                <Droppable droppableId={droppableId}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                                minHeight: '200px',
                                backgroundColor: snapshot.isDraggingOver ? '#ede9fe' : '#f8fafc',
                                borderRadius: '8px',
                                padding: '6px',
                                transition: 'background-color 0.2s ease',
                            }}
                        >
                            {groups.map((group, groupIndex) => {
                                const groupColor = livreurColors[group.livreurId] || '#94a3b8';
                                return (
                                    <div
                                        key={group.livreurId || `group-${groupIndex}`}
                                        style={{
                                            backgroundColor: groupColor + '14',
                                            border: `1px solid ${groupColor}40`,
                                            borderRadius: '8px',
                                            padding: '4px',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {group.items.map(({ delivery, index }) => (
                                            <Draggable key={String(delivery.id)} draggableId={String(delivery.id)} index={index}>
                                                {(provided) => (
                                                    <DeliveryCard
                                                        delivery={delivery}
                                                        provided={provided}
                                                        onClick={() => onCardClick(delivery)}
                                                        onPrepChange={(newStatut) => onPrepChange(delivery.id, newStatut)}
                                                        livreurColors={livreurColors}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                    </div>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        );
    };

    return (
        <div>
            {/* Navigation jour */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '12px 20px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            }}>
                <button
                    onClick={() => onDayChange(addDays(day, -1))}
                    style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '16px' }}
                >
                    ←
                </button>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e', textTransform: 'capitalize' }}>
                        {format(day, 'EEEE d MMMM yyyy', { locale: fr })}
                    </div>
                    {isToday(day) && (
                        <div style={{ fontSize: '12px', color: 'var(--accent-color)', fontWeight: '600' }}>Aujourd'hui</div>
                    )}
                </div>
                <button
                    onClick={() => onDayChange(addDays(day, 1))}
                    style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '16px' }}
                >
                    →
                </button>
            </div>

            {/* Infos du jour */}
            <div style={{ marginBottom: '16px' }}>
                <DayHeader
                    day={day}
                    users={users}
                    dayInfo={dayInfo}
                    teamLocations={dayTeamLocations}
                    onReload={onReload}
                    onPause={() => {}}
                />
            </div>

            {/* Deux colonnes matin / après-midi */}
            <DragDropContext onDragEnd={onDragEnd}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    <PeriodeColumn
                        titre="🌅 Matin"
                        droppableId={`${dateStr}-matin`}
                        items={matin}
                    />
                    <PeriodeColumn
                        titre="🌇 Après-midi"
                        droppableId={`${dateStr}-aprem`}
                        items={aprem}
                    />
                </div>
            </DragDropContext>
        </div>
    );
}